/**
 * The console's one live chat hub socket, mounted in the shell for a
 * browser-session admin so it runs on every page (spec 12.1).
 *
 * Every `conversation.*` frame re-reads the inbox lists, the counts and that
 * chat (a short trailing throttle keeps a burst to one refetch, well inside
 * the 2 s the spec allows); `availability` frames update who is available.
 * A new chat, or a customer message on a chat assigned to me or waiting for a
 * human, shows an in-app toast (with an Open action), plays a short sound and,
 * when the tab is hidden, shows a browser notification. The sound needs an
 * unlocked AudioContext, so the first click or key press in the console
 * creates and resumes it (a tab opened from a Telegram link has had no gesture
 * until then).
 *
 * A conversation frame also writes the customer's presence straight into that
 * chat's cached read, so the open ticket's header flips without a refetch. The tab title carries the number of unread chats.
 */
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { toast } from "@/components/ui/toast";
import { useStaffSocket } from "@/lib/chat-socket";
import { supportChatAvailabilityQuery } from "@/lib/queries";
import {
  chatAlertFor,
  chatToastFor,
  customerPresence,
  summaryPresence,
  unreadChatCount,
  withCustomerPresence,
  withChatCount,
  type ChatAlert,
  type ChatConversationSummary,
  type StaffFrame,
} from "@/lib/support-chat";
import type { ChatAvailabilityView, ChatHistoryPage } from "@/types-support";

const INVALIDATE_AFTER_MS = 250;

let audio: AudioContext | null = null;

/**
 * The shared AudioContext. Browsers keep audio locked until a user gesture, so
 * this is called from gesture handlers (and again before each chime).
 */
export function ensureAudio(): AudioContext | null {
  try {
    const Context =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Context) return null;
    audio ??= new Context();
    void audio.resume();
    return audio;
  } catch {
    return null;
  }
}

/** A short two-note chime, made on the spot: no asset to load or cache. */
function chime(): void {
  try {
    const audio = ensureAudio();
    if (!audio) return;
    const now = audio.currentTime;
    for (const [offset, frequency] of [
      [0, 880],
      [0.14, 1175],
    ] as const) {
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      osc.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, now + offset);
      gain.gain.exponentialRampToValueAtTime(0.12, now + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.25);
      osc.connect(gain).connect(audio.destination);
      osc.start(now + offset);
      osc.stop(now + offset + 0.3);
    }
  } catch {
    // no audio on this device; the title and the list still show it
  }
}

/** Asks once, from a click (browsers refuse a prompt without one). */
export function askForChatNotifications(): void {
  if (typeof Notification === "undefined") return;
  if (Notification.permission === "default") {
    void Notification.requestPermission().catch(() => undefined);
  }
}

function notify(alert: ChatAlert, open: (ticketId: string) => void): void {
  if (
    typeof Notification === "undefined" ||
    Notification.permission !== "granted" ||
    document.visibilityState === "visible"
  ) {
    return;
  }
  try {
    const notification = new Notification(alert.title, {
      body: alert.body.slice(0, 120),
      tag: `chat-${alert.ticketId}`,
    });
    notification.onclick = () => {
      window.focus();
      open(alert.ticketId);
      notification.close();
    };
  } catch {
    // some browsers only allow notifications from a service worker
  }
}

/** The open toast per chat, so arriving at the chat (by Open or any other way) closes it. */
const openToasts = new Map<string, string>();

function showToast(
  alert: ChatAlert,
  created: boolean,
  open: (ticketId: string) => void,
): void {
  const { ticketId, ...fields } = chatToastFor(alert, created);
  const previous = openToasts.get(ticketId);
  if (previous !== undefined) toast.close(previous);
  const id = toast.add({
    ...fields,
    actionProps: {
      children: "Open",
      onClick: () => {
        closeToastFor(ticketId);
        open(ticketId);
      },
    },
    onClose: () => {
      if (openToasts.get(ticketId) === id) openToasts.delete(ticketId);
    },
  });
  openToasts.set(ticketId, id);
}

/** Closes the toast for a chat the admin is now looking at. Exported for tests. */
export function closeToastFor(ticketId: string): void {
  const id = openToasts.get(ticketId);
  if (id === undefined) return;
  openToasts.delete(ticketId);
  toast.close(id);
}

/** The chat id a console path shows, or null. */
export function viewedChatId(pathname: string): string | null {
  const match = /^\/support\/inbox\/([^/]+)$/.exec(pathname);
  return match ? decodeURIComponent(match[1]!) : null;
}

function throttledInvalidate(client: QueryClient) {
  const pending = new Set<string>();
  let timer: ReturnType<typeof setTimeout> | undefined;
  return (ticketId?: string) => {
    if (ticketId) pending.add(ticketId);
    if (timer !== undefined) return;
    timer = setTimeout(() => {
      timer = undefined;
      const ids = [...pending];
      pending.clear();
      void client.invalidateQueries({ queryKey: ["support-inbox"] });
      void client.invalidateQueries({ queryKey: ["support-inbox-counts"] });
      for (const id of ids) {
        void client.invalidateQueries({ queryKey: ["support-ticket", id] });
      }
    }, INVALIDATE_AFTER_MS);
  };
}

export function ChatHub({ meId }: { meId: string }) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const viewing = useRef(pathname);
  viewing.current = pathname;
  // Arriving at a chat closes its toast, however the admin got there.
  useEffect(() => {
    const ticketId = viewedChatId(pathname);
    if (ticketId) closeToastFor(ticketId);
  }, [pathname]);
  const summaries = useRef(new Map<string, ChatConversationSummary>());
  const [invalidate] = useState(() => throttledInvalidate(client));
  const [unread, setUnread] = useState(0);

  const onFrame = (frame: StaffFrame) => {
    if (frame.type === "snapshot") {
      summaries.current = new Map(
        frame.conversations.map((summary) => [summary.ticketId, summary]),
      );
      setUnread(unreadChatCount(summaries.current.values()));
      invalidate();
      return;
    }
    if (
      frame.type === "conversation.created" ||
      frame.type === "conversation.updated"
    ) {
      const next = frame.conversation;
      const previous = summaries.current.get(next.ticketId);
      summaries.current.set(next.ticketId, next);
      setUnread(unreadChatCount(summaries.current.values()));
      invalidate(next.ticketId);
      client.setQueryData<ChatHistoryPage>(
        ["support-chat", next.ticketId],
        (old) => {
          if (!old) return old;
          const kept = customerPresence(old.chat);
          const state = summaryPresence(next);
          return withCustomerPresence(old, {
            state,
            customerOnline: next.customerOnline,
            lastSeenAt: state === "online" ? null : kept.lastSeenAt,
          });
        },
      );
      const alert = chatAlertFor(
        previous,
        next,
        meId,
        frame.type === "conversation.created",
      );
      const onThatChat =
        viewing.current ===
          `/support/inbox/${encodeURIComponent(next.ticketId)}` &&
        document.visibilityState === "visible";
      if (alert && !onThatChat) {
        const open = (ticketId: string) =>
          void navigate(`/support/inbox/${encodeURIComponent(ticketId)}`);
        showToast(alert, frame.type === "conversation.created", open);
        chime();
        notify(alert, open);
      }
      return;
    }
    if (frame.type === "availability") {
      client.setQueryData<ChatAvailabilityView>(
        supportChatAvailabilityQuery().queryKey,
        (old) =>
          old
            ? {
                ...old,
                staff: frame.staff,
                available: frame.staff.some(
                  (row) => row.adminUserId === meId && row.available,
                ),
              }
            : old,
      );
    }
  };

  useStaffSocket({ enabled: true, heartbeat: true, onFrame });

  // Unlock audio on the first gesture so the chime can play later.
  useEffect(() => {
    const unlock = () => {
      ensureAudio();
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  useEffect(() => {
    const apply = () => {
      const next = withChatCount(document.title, unread);
      if (next !== document.title) document.title = next;
    };
    apply();
    const title = document.querySelector("title");
    if (!title) return;
    // Pages set their own titles; this keeps the count in front of them.
    const observer = new MutationObserver(apply);
    observer.observe(title, {
      childList: true,
      characterData: true,
      subtree: true,
    });
    return () => {
      observer.disconnect();
      document.title = withChatCount(document.title, 0);
    };
  }, [unread]);

  return null;
}
